import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, Mic, Sparkles, ArrowRight } from "lucide-react";
import api from "../lib/api";

export const Eyebrow = ({ children }) => (
  <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#4D6BFF] mb-5">{children}</div>
);

// Illustrative quotes by role — swap in real names/titles when available.
const TESTIMONIALS = [
  { quote: "I used to write one big vision memo a year. Now my team hears the why every single week, and it still sounds like me.", role: "Founder" },
  { quote: "My voice notes from the car turn into posts that actually reflect our mission. That used to take my Sunday nights.", role: "Non-Profit Executive" },
  { quote: "Every Monday I get the Pulse, and I feel connected to a vision that keeps evolving. It's a weekly glimpse into how our leader thinks.", role: "Team Member" },
];

function PreviewPanel({ label, icon: Icon, children, accent, delay, testid }) {
  return (
    <div className="landing-rise bg-[#141418] border border-white/10 p-6 sm:p-7 flex-1" style={{ animationDelay: delay }} data-testid={testid}>
      <div className={`flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold mb-5 ${accent}`}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      {children}
    </div>
  );
}

export function ProductPreview() {
  return (
    <section className="py-24 sm:py-32 border-t border-white/10" data-testid="landing-preview">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Eyebrow>Inside VizionCast</Eyebrow>
        <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-none max-w-3xl mb-14">From a 30-second musing to a post that sounds like you.</h2>
        <div className="border border-white/10 bg-[#0F0F12] shadow-[0_40px_120px_-40px_rgba(0,51,255,0.45)]">
          <div className="flex items-center gap-2 px-4 h-10 border-b border-white/10">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF3B30]/70" /><span className="w-2.5 h-2.5 rounded-full bg-[#FFB800]/70" /><span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="ml-3 text-[11px] text-zinc-500 font-mono">vizioncast · content studio</span>
          </div>
          <div className="p-4 sm:p-8 flex flex-col lg:flex-row items-stretch gap-4 lg:gap-0">
            <PreviewPanel label="Voice musing · 0:32" icon={Mic} accent="text-[#FF3B30]" delay="0ms" testid="preview-musing">
              <div className="flex items-end gap-1 h-8 mb-5" aria-hidden="true">
                {[40, 70, 30, 90, 55, 80, 35, 65, 45, 85, 30, 60, 75, 40, 55].map((h, i) => (
                  <span key={i} className="w-1.5 bg-[#FF3B30]/60" style={{ height: `${h}%` }} />
                ))}
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed italic">
                "Customer call today — they didn't care about the feature, they cared that we picked up the phone at 9pm. That's the whole thing. We win on showing up."
              </p>
            </PreviewPanel>
            <div className="flex lg:flex-col items-center justify-center px-4 py-2 gap-2 text-[#4D6BFF]" aria-hidden="true">
              <Sparkles className="w-5 h-5" />
              <ArrowRight className="w-5 h-5 rotate-90 lg:rotate-0" />
            </div>
            <PreviewPanel label="LinkedIn post · ready to review" icon={Sparkles} accent="text-[#4D6BFF]" delay="200ms" testid="preview-post">
              <p className="text-sm text-zinc-200 leading-relaxed mb-3">A customer told me something this week I can't stop thinking about.</p>
              <p className="text-sm text-zinc-200 leading-relaxed mb-3">It wasn't our newest feature that kept them. It was that someone on our team answered the phone at 9pm.</p>
              <p className="text-sm text-zinc-200 leading-relaxed mb-5">Features get copied. Showing up doesn't. That's the company we're building.</p>
              <div className="flex flex-wrap gap-2">
                {["Value: Radical Ownership", "Vision: Trusted by every customer", "Your voice: 94% match"].map((c) => (
                  <span key={c} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-[#0033FF]/50 text-[#8FA3FF]">{c}</span>
                ))}
              </div>
            </PreviewPanel>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  return (
    <section className="py-24 sm:py-32 bg-[#0F0F12] border-t border-white/10" data-testid="landing-testimonials">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Eyebrow>What You'll Hear</Eyebrow>
        <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-none mb-14 max-w-3xl">…when your company starts VizionCasting.</h2>
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} className="bg-[#0F0F12] p-8 sm:p-10 flex flex-col" data-testid={`landing-testimonial-${i}`}>
              <div className="text-6xl leading-none font-black text-[#0033FF] mb-4" aria-hidden="true">"</div>
              <blockquote className="text-lg text-zinc-200 leading-relaxed flex-1">{t.quote}</blockquote>
              <figcaption className="mt-8 pt-6 border-t border-white/10">
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#4D6BFF]" data-testid={`landing-testimonial-role-${i}`}>{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

const ROWS = [
  ["Musings: note, voice & photo", (p) => true],
  ["AI generations", (p) => (p.ai_prompts_per_month == null ? "Unlimited" : `${p.ai_prompts_per_month} / month`)],
  ["Content Studio", (p) => (p.content_types_allowed ? "Posts & emails" : "Every format")],
  ["Daily & weekly prompts", (p) => (p.editable_prompts ? "Editable" : "Fixed set")],
  ["Team & client database", (p) => (p.team_db_limit == null ? "Unlimited" : `${p.team_db_limit} each`)],
  ["Weekly Vision Pulse + email", (p) => !!p.vision_pulse],
  ["Personalized notes", (p) => !!p.personal_notes],
  ["Team seats", (p) => (p.base_seats ? `${p.base_seats} included, up to ${p.max_seats}` : "Just you")],
  ["Per-seat controls & shared profile", (p) => !!p.base_seats],
];

const Cell = ({ v }) => (v === true ? <Check className="w-4 h-4 text-[#4D6BFF] mx-auto" /> : v === false ? <Minus className="w-4 h-4 text-zinc-600 mx-auto" /> : <span className="text-sm text-zinc-300">{v}</span>);

function priceOf(p, yearly) {
  if (p.price_monthly_base) return { amount: `$${p.price_monthly_base}`, per: "/mo", note: `+$${p.price_per_extra_seat.toFixed(2)} per seat after ${p.base_seats}` };
  if (!p.price_monthly) return { amount: "Free", per: "", note: "Forever" };
  return yearly ? { amount: `$${p.price_yearly}`, per: "/yr", note: `Save $${p.price_monthly * 12 - p.price_yearly} a year` } : { amount: `$${p.price_monthly}`, per: "/mo", note: "Billed monthly" };
}

export function Pricing() {
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  useEffect(() => { api.get("/billing/plans").then(({ data }) => setPlans(data.plans)).catch(() => {}); }, []);

  return (
    <section id="pricing" className="py-24 sm:py-32 border-t border-white/10" data-testid="landing-pricing">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-none">Pick your cadence.</h2>
          </div>
          <div className="inline-flex border border-white/15" data-testid="pricing-cycle-toggle">
            {[["Monthly", false], ["Yearly", true]].map(([l, v]) => (
              <button key={l} onClick={() => setYearly(v)} data-testid={`pricing-cycle-${l.toLowerCase()}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${yearly === v ? "bg-white text-[#0A0A0B]" : "text-zinc-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>
        {plans.length === 0 ? <div className="h-96 border border-white/10 animate-pulse" data-testid="pricing-loading" /> : (
          <div className="overflow-x-auto -mx-5 sm:mx-0 px-5 sm:px-0">
            <table className="w-full min-w-[720px] border-collapse" data-testid="pricing-table">
              <thead>
                <tr>
                  <th className="w-[28%]" />
                  {plans.map((p) => {
                    const pr = priceOf(p, yearly);
                    const hot = p.key === "executive";
                    return (
                      <th key={p.key} className={`text-left align-top p-6 border border-white/10 ${hot ? "bg-[#0033FF]" : "bg-[#0F0F12]"}`} data-testid={`pricing-plan-${p.key}`}>
                        <div className={`text-[10px] tracking-[0.25em] uppercase font-bold mb-3 ${hot ? "text-white/80" : "text-[#4D6BFF]"}`}>{hot ? "Most popular" : "\u00a0"}</div>
                        <div className="text-lg font-black text-white -mt-1 mb-1">{p.name}</div>
                        <div className="flex items-baseline gap-1"><span className="text-4xl font-black text-white" data-testid={`pricing-amount-${p.key}`}>{pr.amount}</span><span className="text-sm text-white/60">{pr.per}</span></div>
                        <div className={`text-xs mt-1 font-normal ${hot ? "text-white/80" : "text-zinc-500"}`}>{pr.note}</div>
                        <p className={`text-sm font-normal mt-4 ${hot ? "text-white/90" : "text-zinc-400"}`}>{p.tagline}</p>
                        <Link to="/register" data-testid={`pricing-cta-${p.key}`}
                          className={`mt-6 inline-flex w-full justify-center px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${hot ? "bg-white text-[#0A0A0B] hover:bg-[#0A0A0B] hover:text-white" : "border border-white/20 text-white hover:bg-white hover:text-[#0A0A0B]"}`}>
                          {p.price_monthly === 0 ? "Start free" : "Get started"}
                        </Link>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, fn]) => (
                  <tr key={label} className="hover:bg-white/[0.03] transition-colors">
                    <td className="py-4 pr-4 text-sm text-zinc-400 border-b border-white/10">{label}</td>
                    {plans.map((p) => <td key={p.key} className="py-4 px-6 text-center border-b border-x border-white/10"><Cell v={fn(p)} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-zinc-600 mt-4">Prices in USD. Taxes calculated at checkout. Cancel anytime.</p>
      </div>
    </section>
  );
}
